import { uniq, isEmpty } from 'lodash';
import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import OrderCard from '../../Orders/OrderCard';
import Modal from '../../../Modal';
import Radio from '../../../Radio';
import Checkbox from '../../../Checkbox';
import Input from '../../../Input';
import RadioGroup from '../../../RadioGroup';
import Button from '../../../Button';
import OnClickOutside from '../../../OnClickOutside';

import { ORDER_STATUSES } from '../../Orders/OrderStatus/constants';

import { FieldClose as CloseIcon } from '@icons/common';

import ICloseReason from '../interfaces/ICloseReason';
import IOrder from '../interfaces/IOrder';
import { OrderOptionType } from '../types';

const STATUS = {
  SUCCESS: 'success',
  FAILURE: 'failure',
};

interface Props {
  className?: string;
  reasonId?: string;
  isClosable: boolean;
  orderIds?: string[];
  orderOptions: OrderOptionType[];
  suggestedOrders?: OrderOptionType[];
  reasonOptions: ICloseReason[];
  onOrderSearch: (query) => void;
  onReasonChange?: (reason) => void;
  onOrderChange?: (reason) => void;
  onClose?: () => void;
  onSubmit?: (params: { orderIds: unknown; closeReasonId: string }) => void;
}

const TicketCloseReason = (props: Props) => {
  const {
    className = '',
    reasonId: prefilledReasonId,
    orderIds: prefilledOrderIds,
    isClosable,
    reasonOptions,
    orderOptions,
    suggestedOrders,
    onOrderSearch = Function.prototype,
    onClose = Function.prototype,
    onSubmit = Function.prototype,
    onReasonChange = Function.prototype,
    onOrderChange = Function.prototype,
  } = props;

  const [isForceHidden, setForceHidden] = useState(false);

  const [reasonId, setReason] = useState(prefilledReasonId || null);
  const [orderIds, setOrderIds] = useState(prefilledOrderIds || []);

  const [status, setCloseStatus] = useState(null);

  const activeReasonOptions = useMemo(
    () => reasonOptions?.filter(({ isActive }) => isActive),
    [reasonOptions, reasonOptions?.length]
  );

  const handleCloseStatus = useCallback(
    ({ target }) => {
      setCloseStatus(target.value);
    },
    [setReason, setOrderIds, setCloseStatus]
  );

  const handleCloseModal = useCallback(() => {
    setForceHidden(true);
    onClose();
  }, [onClose, setForceHidden]);

  const handleReasonChange = useCallback(
    ({ target }) => {
      setReason(target.value);
    },
    [onReasonChange]
  );

  const handleOrderChange = useCallback(
    ({ target }) => {
      const { value } = target;
      const updatedOrderIds = orderIds.includes(value)
        ? orderIds.filter(id => id !== value)
        : [...orderIds, value];
      setOrderIds(uniq(updatedOrderIds));
    },
    [orderIds, onOrderChange]
  );

  const [ordersInput, setOrdersInput] = useState('');

  const showOwnOrders = ordersInput.length > 2;

  const handleOrdersInput = useCallback(
    ({ target }) => {
      const query = target.value;
      setOrdersInput(query);

      if (showOwnOrders) {
        onOrderSearch({ query });
      }
    },
    [showOwnOrders, onOrderSearch]
  );

  const handleSubmit = useCallback(() => {
    if (status === STATUS.SUCCESS) {
      onSubmit({
        orderIds,
      });
    }

    if (status === STATUS.FAILURE) {
      onSubmit({
        closeReasonId: reasonId,
      });
    }
  }, [status, orderIds, reasonId, onSubmit]);

  const orders = showOwnOrders ? suggestedOrders : orderOptions;

  if (!isClosable) {
    return !isForceHidden ? (
      <Modal shadow={false}>
        <OnClickOutside onClickOutside={handleCloseModal}>
          <StyledTicketCloseReason className={className}>
            <StyledHead>
              <StyledTitle>Требуется заполнить причину обращения</StyledTitle>
              <Button functional onClick={handleCloseModal}>
                <CloseIcon width={14} />
              </Button>
            </StyledHead>
            <StyledBottom>
              <StyledSubmitButton onClick={handleCloseModal}>
                Продолжить
              </StyledSubmitButton>
            </StyledBottom>
          </StyledTicketCloseReason>
        </OnClickOutside>
        <StyledShadow />
      </Modal>
    ) : null;
  }

  return !isForceHidden ? (
    <Modal shadow={false}>
      <OnClickOutside onClickOutside={handleCloseModal}>
        <StyledTicketCloseReason className={className}>
          <StyledHead>
            <StyledTitle>
              {reasonId ? 'Закрытие тикета' : 'Причина закрытия'}
            </StyledTitle>
            <Button functional onClick={handleCloseModal}>
              <CloseIcon width={14} />
            </Button>
          </StyledHead>
          {!status && (
            <StyledBottom>
              <StyledFailureButton
                value={STATUS.FAILURE}
                onClick={handleCloseStatus}
              >
                Клиент отказался
              </StyledFailureButton>
              <StyledSuccessButton
                value={STATUS.SUCCESS}
                onClick={handleCloseStatus}
              >
                Клиент забронировал заказ
              </StyledSuccessButton>
            </StyledBottom>
          )}
          {status === STATUS.SUCCESS && (
            <StyledOrders>
              <StyledOrdersInputContainer>
                <StyledOrdersInput
                  placeholder="Поиск заказов"
                  onChange={handleOrdersInput}
                />
              </StyledOrdersInputContainer>
              {orders &&
                orders.map(
                  ({ id, status: orderStatus, ...order }) =>
                    orderStatus !== ORDER_STATUSES.CANCELED && (
                      <StyledOrderRadio
                        key={id}
                        value={id}
                        onChange={handleOrderChange}
                        checked={orderIds.includes(id)}
                      >
                        <OrderCard
                          id={id}
                          status={orderStatus}
                          {...order}
                          tickets={null}
                        />
                      </StyledOrderRadio>
                    )
                )}
            </StyledOrders>
          )}
          {status === STATUS.FAILURE && (
            <StyledReasons
              name="reasons"
              onChange={handleReasonChange}
              selected={reasonId}
            >
              {activeReasonOptions.map(({ value, label }) => (
                <StyledReasonRadio
                  key={value}
                  value={String(value)}
                  label={label}
                >
                  {label}
                </StyledReasonRadio>
              ))}
            </StyledReasons>
          )}
          {status && (
            <StyledBottom>
              <StyledBackButton value={null} onClick={handleCloseStatus}>
                Назад
              </StyledBackButton>
              <StyledSubmitButton
                disabled={!(reasonId || !isEmpty(orderIds))}
                onClick={handleSubmit}
              >
                Продолжить
              </StyledSubmitButton>
            </StyledBottom>
          )}
        </StyledTicketCloseReason>
      </OnClickOutside>
      <StyledShadow />
    </Modal>
  ) : null;
};

const StyledHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 16px;
`;

const StyledBottom = styled.div`
  display: flex;
  padding: 0 16px 16px;
`;

const StyledFailureButton = styled(Button)`
  color: #d0021b;
  border-color: #d0021b;
  width: fit-content;
  margin-right: 7px;
  background: white;

  :hover {
    border-color: #d0021b;
    color: #d0021b;
  }
`;

const StyledSuccessButton = styled(Button)`
  color: white;
  background: #28bd61;
  border-color: transparent;
  width: fit-content;

  :hover {
    border-color: transparent;
    color: white;
  }
`;

const StyledTitle = styled.h4`
  font-size: 18px;
  font-weight: bold;
  margin-right: 10px;
`;

const StyledBackButton = styled(Button)`
  border-color: #007aff;
  background: white;
  margin-right: 7px;
  flex: 1;

  :hover {
    background: white;
  }
`;

const StyledSubmitButton = styled(Button)`
  border-color: transparent;
  background: #007aff;
  color: white;
  flex: 1;

  :hover {
    color: white;
    background: #007aff;
  }

  ${props =>
    props.disabled &&
    `
    border-color: transparent;
    color: #919499;
    background: #E6E9EE;

    :hover {
      color: #919499;
      background: #E6E9EE;
    }
  `}
`;

const StyledOrders = styled.div`
  width: 400px;
  margin-bottom: 16px;
  max-height: calc(100vh - 400px);
  overflow: auto;
`;

const StyledOrderRadio = styled(Checkbox)`
  padding: 5px 16px;
  align-items: flex-start;

  input {
    margin-right: 10px;
  }

  :hover {
    background: #e6f2ff;
  }
`;

const StyledReasonRadio = styled(Radio)`
  padding: 0 10px;
  min-height: 40px;
  border: 1px solid #e6e9ee;
  border-radius: 4px;
  margin-bottom: 8px;
  margin-right: 8px;
  width: calc(50% - 4px);

  :hover {
    background: #e6f2ff;
  }

  input {
    display: none;
  }

  ${props =>
    props.checked &&
    `
    color: white;
    background: #007AFF;
    border: transparent;

    :hover {
      color: white;
      background: #007AFF;
    }
  `}
`;

const StyledReasons = styled(RadioGroup)`
  width: 500px;
  padding: 0 16px;
  margin-bottom: 8px;
  flex-flow: row wrap;
  display: flex;
  max-height: calc(100vh - 400px);
  overflow: auto;

  ${StyledReasonRadio} {
    &:nth-child(2n) {
      margin-right: 0;
    }
  }
`;

const StyledTicketCloseReason = styled.div`
  position: relative;
  background: white;
  width: fit-content !important;
  border-radius: 4px;
  margin-top: 150px !important;
  margin-left: auto !important;
  margin-right: auto !important;
  left: 0;
  right: 0;
  z-index: 99;
`;

const StyledShadow = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  position: fixed;
  touch-action: none;
  background-color: rgba(0, 0, 0, 0.2);
  -webkit-tap-highlight-color: transparent;
`;

const StyledOrdersInput = styled(Input)`
  width: 100%;
`;

const StyledOrdersInputContainer = styled.div`
  padding: 0 15px 15px;
`;

export default TicketCloseReason;
